import React, { useEffect, useState } from "react";
import { CalciteCheckbox, CalciteLabel, CalciteIcon, CalciteLoader } from "@esri/calcite-components-react";
import "./styles/DisclaimerModal.css";

type Props = {
    isOpen: boolean;
    onAccept: () => void;
    onDecline: () => void;
};

const GUIDE_SHOWN_KEY = "psa_guide_shown";

const DisclaimerModal: React.FC<Props> = ({ isOpen, onAccept, onDecline }) => {
    const [html, setHtml] = useState("");
    const [showGuide, setShowGuide] = useState(false);
    const [dontShowAgain, setDontShowAgain] = useState(false);
    const [isZoomed, setIsZoomed] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [calciteReady, setCalciteReady] = useState(false);

    useEffect(() => {
        if (!isOpen) return;

        fetch(`${process.env.PUBLIC_URL}/disclaimer.html`)
            .then(async (r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                setHtml(await r.text());
            })
            .catch((err) => {
                console.error("Failed to load disclaimer:", err);
                setHtml("<p>Disclaimer content not available.</p>");
            });
    }, [isOpen]);

    // Wait for Calcite components to be defined
    useEffect(() => {
        const checkCalciteComponents = async () => {
            if (customElements.get('calcite-label') && customElements.get('calcite-checkbox')) {
                setCalciteReady(true);
            } else {
                await Promise.all([
                    customElements.whenDefined('calcite-label'),
                    customElements.whenDefined('calcite-checkbox')
                ]);
                setCalciteReady(true);
            }
        };
        checkCalciteComponents();
    }, []);

    const handleAcceptDisclaimer = () => {
        // Check if user has already seen the guide
        const guideShown = localStorage.getItem(GUIDE_SHOWN_KEY);

        if (guideShown === "true") {
            // Skip guide, go straight to app
            onAccept();
        } else {
            // Only show guide if Calcite components are ready
            if (calciteReady) {
                setShowGuide(true);
            } else {
                // If not ready, wait for them to load then show guide
                Promise.all([
                    customElements.whenDefined('calcite-label'),
                    customElements.whenDefined('calcite-checkbox')
                ]).then(() => {
                    setCalciteReady(true);
                    setShowGuide(true);
                });
            }
        }
    };

    const handleCloseGuide = () => {
        if (dontShowAgain) {
            localStorage.setItem(GUIDE_SHOWN_KEY, "true");
        }
        onAccept();
    };

    const handleImageClick = () => {
        setIsZoomed(true);
        setZoomLevel(1);
    };

    const handleZoomIn = () => {
        setZoomLevel((prev) => Math.min(prev + 0.5, 3));
    };

    const handleZoomOut = () => {
        setZoomLevel((prev) => Math.max(prev - 0.5, 0.5));
    };

    const handleCloseZoom = () => {
        setIsZoomed(false);
        setZoomLevel(1);
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="disc-backdrop">
                <div className="disc-modal">
                    {!showGuide ? (
                        <>
                            <h2 className="disc-title">Disclaimer</h2>

                            <div
                                className="disc-body"
                                dangerouslySetInnerHTML={{ __html: html }}
                            />

                            <div className="disc-footer">
                                <button className="disc-btn disc-btn-secondary" onClick={onDecline}>
                                    Decline
                                </button>
                                <button className="disc-btn disc-btn-primary" onClick={handleAcceptDisclaimer}>
                                    Accept
                                </button>
                            </div>
                        </>
                    ) : calciteReady ? (
                        <>
                            <h2 className="disc-title">Application Guide</h2>

                            <div className="disc-body" style={{ textAlign: 'center' }}>
                                <div
                                    onClick={handleImageClick}
                                    style={{
                                        cursor: 'pointer',
                                        display: 'inline-block',
                                        position: 'relative'
                                    }}
                                    title="Click to zoom"
                                >
                                    <img
                                        src={`${process.env.PUBLIC_URL}/HarrisonMS_PSA_Splash_2026.png`}
                                        alt="How to use guide"
                                        style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        bottom: '10px',
                                        right: '10px',
                                        background: 'rgba(0, 121, 193, 0.9)',
                                        color: 'white',
                                        padding: '6px 12px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        pointerEvents: 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <CalciteIcon icon="magnifying-glass" scale="s" />
                                        Click to zoom
                                    </div>
                                </div>
                            </div>

                            <div className="disc-footer" style={{ flexDirection: 'column', gap: '12px' }}>
                                <CalciteLabel layout="inline">
                                    <CalciteCheckbox
                                        checked={dontShowAgain}
                                        onCalciteCheckboxChange={(e) => setDontShowAgain(e.target.checked)}
                                    />
                                    Don't show this again
                                </CalciteLabel>
                                <button className="disc-btn disc-btn-primary" onClick={handleCloseGuide}>
                                    Close
                                </button>
                            </div>
                        </>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px' }}>
                            <CalciteLoader label="Loading application guide" scale="m" />
                        </div>
                    )}
                </div>
            </div>

            {isZoomed && (
                <div className="image-zoom-overlay" onClick={handleCloseZoom}>
                    <div className="image-zoom-controls">
                        <button
                            className="zoom-btn"
                            onClick={(e) => { e.stopPropagation(); handleZoomOut(); }}
                            title="Zoom Out"
                            disabled={zoomLevel <= 0.5}
                        >
                            −
                        </button>
                        <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
                        <button
                            className="zoom-btn"
                            onClick={(e) => { e.stopPropagation(); handleZoomIn(); }}
                            title="Zoom In"
                            disabled={zoomLevel >= 3}
                        >
                            +
                        </button>
                        <button
                            className="zoom-btn zoom-close"
                            onClick={handleCloseZoom}
                            title="Close"
                        >
                            ×
                        </button>
                    </div>
                    <div className="image-zoom-container" onClick={(e) => e.stopPropagation()}>
                        <div style={{
                            minWidth: `max(100%, ${zoomLevel * 90}vw)`,
                            minHeight: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '20px',
                            boxSizing: 'border-box'
                        }}>
                            <img
                                src={`${process.env.PUBLIC_URL}/HarrisonMS_PSA_Splash_2026.png`}
                                alt="How to use guide (zoomed)"
                                style={{
                                    display: 'block',
                                    width: `${zoomLevel * 90}vw`,
                                    height: 'auto',
                                    transition: 'width 0.2s ease',
                                    flexShrink: 0
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default DisclaimerModal;
